// Actuals: reported hours from calendar events (PRD §3.8, AC1–AC9). Pure functions shared by the
// API, which recomputes on every write, and the browser, which previews an import before it runs.
//
// Everything is in US Central, where HQ is (§3.1, rev. 1.9): an event is split into days at
// midnight Central, weeks start on Monday in Central, and a week freezes at its cutoff, Tuesday
// 12:00 Central after it ends (AC9). Times are stored in UTC.

import { addDays, eachDay, isWeekday, mondayOf, type Iso } from "./dates";
import { isValidCode, normalizeCode } from "./projects";

export const HQ_TIME_ZONE = "America/Chicago";

// --- Time zones ------------------------------------------------------------------------------------

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(timeZone: string) {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    formatters.set(timeZone, f);
  }
  return f;
}

/** The wall clock in `timeZone` at an instant: its date, and milliseconds since that date's midnight. */
export function wallClock(ms: number, timeZone: string = HQ_TIME_ZONE): { day: Iso; msOfDay: number } {
  const parts = Object.fromEntries(formatter(timeZone).formatToParts(new Date(ms)).map((p) => [p.type, p.value]));
  const day = `${parts.year}-${parts.month}-${parts.day}`;
  const msOfDay = ((Number(parts.hour) * 60 + Number(parts.minute)) * 60 + Number(parts.second)) * 1000 + (ms % 1000 + 1000) % 1000;
  return { day, msOfDay };
}

/** Milliseconds the zone is ahead of UTC at an instant (Central: −5 h in summer, −6 h in winter). */
function offsetMs(ms: number, timeZone: string): number {
  const { day, msOfDay } = wallClock(ms, timeZone);
  return Date.parse(`${day}T00:00:00Z`) + msOfDay - ms;
}

/** The instant a wall-clock time in `timeZone` names (two passes settle the daylight-saving offset). */
export function zonedToUtc(day: Iso, msOfDay: number, timeZone: string = HQ_TIME_ZONE): number {
  const wall = Date.parse(`${day}T00:00:00Z`) + msOfDay;
  const guess = wall - offsetMs(wall, timeZone);
  return wall - offsetMs(guess, timeZone);
}

/** Midnight Central at the start of a day, as an instant. */
export const centralMidnight = (day: Iso): number => zonedToUtc(day, 0, HQ_TIME_ZONE);

// --- Reading source values -------------------------------------------------------------------------

const EXCEL_EPOCH = Date.UTC(1899, 11, 30);

/**
 * A Start or End Time cell as an instant. An ISO time with `Z` or an offset says its own zone. A time
 * without one (Power Automate writes "2026-01-05T15:00:00.0000000"), a US-style "1/5/2026 3:00 PM",
 * or an Excel date serial is read as wall time in `sourceZone`.
 */
export function parseSourceTime(value: unknown, sourceZone: string): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value < 36_526 || value > 73_050) return null; // 2000–2099
    const wall = EXCEL_EPOCH + Math.round(value * 86_400_000);
    const day = new Date(wall).toISOString().slice(0, 10);
    return zonedToUtc(day, wall - Date.parse(`${day}T00:00:00Z`), sourceZone);
  }
  if (typeof value !== "string") return null;
  const t = value.trim();
  let m = t.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/i);
  if (m) {
    const [, day, h, min, s, zone] = m;
    const msOfDay = ((Number(h) * 60 + Number(min)) * 60 + Number(s ?? 0)) * 1000;
    if (zone) {
      const ms = Date.parse(`${day}T${h.padStart(2, "0")}:${min}:${s ?? "00"}${zone.toUpperCase() === "Z" ? "Z" : zone.replace(/^([+-]\d{2})(\d{2})$/, "$1:$2")}`);
      return Number.isNaN(ms) ? null : ms;
    }
    return zonedToUtc(day, msOfDay, sourceZone);
  }
  m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AP]M)?)?$/i);
  if (m) {
    const [, mo, d, y, h = "0", min = "0", s = "0", ampm] = m;
    let hour = Number(h);
    if (ampm) hour = (hour % 12) + (ampm.toUpperCase() === "PM" ? 12 : 0);
    const day = `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
    if (Number.isNaN(Date.parse(`${day}T00:00:00Z`))) return null;
    return zonedToUtc(day, ((hour * 60 + Number(min)) * 60 + Number(s)) * 1000, sourceZone);
  }
  return null;
}

/** Outlook's free/busy status ("show as"). */
export type ShowAs = "busy" | "tentative" | "free" | "oof" | "workingElsewhere" | "unknown";

export function parseShowAs(value: unknown): ShowAs {
  const t = String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
  if (t === "busy") return "busy";
  if (t === "tentative") return "tentative";
  if (t === "free") return "free";
  if (t === "oof" || t === "outofoffice" || t === "away") return "oof";
  if (t === "workingelsewhere") return "workingElsewhere";
  return "unknown";
}

/** Busy, Tentative and a timed Out of office are time spent (§3.8, rev. 1.10). Free is visibility only. */
export const COUNTED_STATUSES: ReadonlySet<ShowAs> = new Set(["busy", "tentative", "oof"]);

/** Time-off codes (§3.8). A timed block with one of these says what an all-day Out of office was for. */
export const TIME_OFF_CODES: ReadonlySet<string> = new Set(["3SVAC", "3SHOL", "3SSIC", "3SUTO"]);
/** Unpaid time off, negotiated (rev. 1.16): every use goes to the inbox to review. */
export const UNPAID_CODE = "3SUTO";

export function parseBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  return /^(true|yes|y|1)$/i.test(String(value ?? "").trim());
}

/** A categories cell: "LG002, Build", `["LG002","Build"]`, or one name. */
export function parseCategories(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).map((s) => s.trim()).filter(Boolean);
  return String(value ?? "")
    .replace(/^\s*\[|\]\s*$/g, "")
    .split(/[,;]/)
    .map((s) => s.trim().replace(/^["']|["']$/g, "").trim())
    .filter(Boolean);
}

// --- Counting --------------------------------------------------------------------------------------

/** A calendar event as either source gives it, already in UTC. */
export interface SourceEvent {
  /** Stable for the same event: the Graph occurrence id, or a fingerprint of the imported row. */
  key: string;
  startMs: number;
  endMs: number;
  subject: string;
  categories: string[];
  showAs: ShowAs;
  isAllDay: boolean;
  isCancelled?: boolean;
  /** R&D activity: the Timecard Tool's Sub-Category on import (§3.8). */
  activity?: string;
}

export type EntryFlag = "uncategorized" | "unknown-code" | "multi-code" | "overlap" | "long-day" | "oof-unblocked" | "outlook-changed" | "unpaid-time-off" | "no-request" | "under-reported" | "no-chargeline" | "not-on-calendar";

export const FLAG_TEXT: Readonly<Record<EntryFlag, string>> = {
  uncategorized: "No category",
  "unknown-code": "Category isn't a project code",
  "multi-code": "More than one project code; hours split evenly",
  overlap: "Overlaps another event",
  "long-day": "Day over 12 hours",
  "oof-unblocked": "Out of office all day, with no timed block saying what for",
  "outlook-changed": "Changed in Outlook after the week froze",
  "unpaid-time-off": "Unpaid time off (3SUTO): lowers the month's cost; review",
  "no-request": "Time off with no approved request",
  "under-reported": "Fewer hours reported than expected for the week",
  "no-chargeline": "Project isn't booked in Base Camp on this day, or the day is outside its contract: no active chargeline",
  "not-on-calendar": "Time off was requested, but nothing is on the calendar. Approve if it was taken as requested; dismiss if it wasn't taken",
};

/**
 * The day time entries start needing a booked project (Chris, 2026-10-23: every time card up to then has
 * been reconciled). The Monday of the week that was still open, so its entries are checked when it
 * freezes. Earlier entries keep the codes they were reconciled to.
 */
export const CHARGELINE_CHECK_FROM: Iso = "2026-10-19";

/** Time off and personal time (xx-codes) aren't billed, so they never need a chargeline. */
export const needsChargeline = (code: string): boolean => !TIME_OFF_CODES.has(code) && !/^XX\d{3}$/i.test(code);

/** How an admin settled an inbox item. It holds whatever the calendar does later. */
export interface Resolution {
  /** The codes the hours go to, split evenly; empty means "not work time", so the hours don't count. */
  codes: string[];
  note?: string;
  /** Accepted as reported: the flag (an overlap, say) was not a problem, and every code counts (rev. 1.14). */
  approved?: true;
  by: string;
  at: string;
}

/** One day's piece of a counted event (an event over midnight Central becomes two). */
export interface TimeEntry {
  /** `${event key}#${day}`; a block an admin added is `man:${uuid}#${day}`. */
  id: string;
  /** `manual`: added by an admin on the time card (rev. 1.12); never touched by an import or sync. */
  source: "import" | "graph" | "manual";
  /** The event's start and end in UTC, as ISO strings; `hours` is the part on `day`. */
  start: string;
  end: string;
  day: Iso;
  hours: number;
  /** Project codes the categories map to. */
  codes: string[];
  categories: string[];
  showAs: ShowAs;
  subject: string;
  activity?: string;
  flags: EntryFlag[];
  resolution?: Resolution;
  /**
   * An admin moved or resized the block on the time card (rev. 1.12): `start`, `end` and `hours` above
   * are the new ones, these the calendar's. An import or sync keeps the admin's times.
   */
  retimed?: { start: string; end: string; hours: number; by: string; at: string };
  /**
   * The calendar sync found this entry changed in Outlook after its week froze (AC1, P2b). The entry
   * keeps counting as recorded until an admin accepts Outlook's version or keeps the record.
   */
  outlook?: OutlookChange;
  /** The difference an admin chose to keep as recorded, so the next sync doesn't raise it again. */
  outlookKept?: string;
  /**
   * An all-day vacation or sick event (Chris, 2026-10-29): zero hours here, as all-day events always are,
   * but time off counts the day as 8 h.
   */
  allDay?: true;
}

/** What Outlook says now about an entry in a frozen week (AC1). An `added` entry counts 0 h until accepted. */
export interface OutlookChange {
  kind: "deleted" | "changed" | "added";
  /** Identifies this exact difference, so keeping the record silences it until Outlook changes again. */
  sig: string;
  /** When the sync first saw it. */
  seen: string;
  /** Outlook's version, for changed and added. */
  now?: { start: string; end: string; hours: number; categories: string[]; codes: string[] };
}

/** One change made on a time card or in the inbox, kept with the week (rev. 1.12). */
export interface TimeChange {
  at: string;
  by: string;
  byOid: string;
  /** Made by an admin on someone else's time card, or after the week froze. */
  administrative: boolean;
  entryId: string;
  kind: "recode" | "not-work" | "approve" | "reset" | "retime" | "add" | "remove" | "outlook-accept" | "outlook-keep" | "resync";
  /** Plain words: "Tue Oct 6, 9:00–10:00: EX001 → AN001". */
  summary: string;
  note?: string;
}

export type Exclusion = "all-day" | "status" | "cancelled" | "personal" | "no-length";

export const EXCLUSION_TEXT: Readonly<Record<Exclusion, string>> = {
  "all-day": "All-day event",
  status: "Shown as Free or Working elsewhere",
  cancelled: "Cancelled",
  personal: "Personal",
  "no-length": "Ends at or before it starts",
};

/** Category names to project codes. The Project Categories Index list adds aliases in P2b (AC3). */
export interface CodeIndex {
  known: ReadonlySet<string>;
  aliases?: ReadonlyMap<string, string>;
}

/** A category's project code: an alias, the code itself, or a known code leading the name ("LG002 – Logic"). */
export function codeForCategory(category: string, index: CodeIndex): string | null {
  const alias = index.aliases?.get(category.trim().toLowerCase());
  if (alias) return alias;
  const code = normalizeCode(category);
  if (isValidCode(code) && index.known.has(code)) return code;
  const lead = category.trim().toUpperCase().match(/^([A-Z0-9]{2,12})\b/)?.[1];
  return lead && index.known.has(lead) ? lead : null;
}

const isPersonal = (c: string) => c.trim().toLowerCase() === "personal";

/**
 * An event to its entries, one per Central day it touches, or the reason it doesn't count (§3.8).
 * Day-level flags (overlap, long day) are added by `flagDays` once a person's entries are together.
 */
export function countEvent(e: SourceEvent, source: TimeEntry["source"], index: CodeIndex): { entries: TimeEntry[] } | { excluded: Exclusion } {
  if (e.isAllDay) return { excluded: "all-day" };
  if (e.isCancelled) return { excluded: "cancelled" };
  if (!COUNTED_STATUSES.has(e.showAs)) return { excluded: "status" };
  if (!(e.endMs > e.startMs)) return { excluded: "no-length" };
  const categories = e.categories.filter((c) => !isPersonal(c));
  if (e.categories.length && !categories.length) return { excluded: "personal" };
  const codes = [...new Set(categories.map((c) => codeForCategory(c, index)).filter((c): c is string => !!c))];
  const flags: EntryFlag[] = [];
  if (!categories.length) flags.push("uncategorized");
  // Once a code matched, an extra category (an R&D activity such as Build) is kept but not flagged (rev. 1.12).
  else if (!codes.length) flags.push("unknown-code");
  if (codes.length > 1) flags.push("multi-code");
  if (codes.includes(UNPAID_CODE)) flags.push("unpaid-time-off");
  const entries: TimeEntry[] = [];
  for (let from = e.startMs; from < e.endMs; ) {
    const { day } = wallClock(from);
    const next = Math.min(e.endMs, centralMidnight(addDays(day, 1)));
    entries.push({
      id: `${e.key}#${day}`,
      source,
      start: new Date(e.startMs).toISOString(),
      end: new Date(e.endMs).toISOString(),
      day,
      hours: round((next - from) / 3_600_000),
      codes,
      categories,
      showAs: e.showAs,
      subject: e.subject,
      ...(e.activity ? { activity: e.activity } : {}),
      flags: [...flags],
    });
    from = next;
  }
  return { entries };
}

const round = (h: number) => Math.round(h * 10_000) / 10_000;

/**
 * The day-level flags (§3.8, AC6): overlapping events, and a day over 12 hours. Entries are one person's.
 * An entry marked not work time counts no hours, so it neither overlaps anything nor lengthens the day (Chris, 2026-10-27).
 */
export function flagDays(entries: TimeEntry[]): TimeEntry[] {
  const out = entries.map((e) => ({ ...e, flags: e.flags.filter((f) => f !== "overlap" && f !== "long-day") }));
  const byDay = new Map<Iso, TimeEntry[]>();
  for (const e of out) if (!(e.resolution && !e.resolution.codes.length)) byDay.set(e.day, [...(byDay.get(e.day) ?? []), e]);
  for (const day of byDay.values()) {
    const span = (e: TimeEntry) => {
      const dayStart = centralMidnight(e.day);
      return [Math.max(Date.parse(e.start), dayStart), Math.min(Date.parse(e.end), centralMidnight(addDays(e.day, 1)))];
    };
    const sorted = [...day].sort((a, b) => span(a)[0] - span(b)[0]);
    let reach = -Infinity;
    let reacher: TimeEntry | null = null;
    for (const e of sorted) {
      if (e.hours === 0) continue; // a placeholder (all-day Out of office, an event added after the freeze) overlaps nothing
      const [s, end] = span(e);
      if (s < reach && reacher) {
        if (!e.flags.includes("overlap")) e.flags.push("overlap");
        if (!reacher.flags.includes("overlap")) reacher.flags.push("overlap");
      }
      if (end > reach) {
        reach = end;
        reacher = e;
      }
    }
    if (day.reduce((t, e) => t + e.hours, 0) > 12) for (const e of day) e.flags.push("long-day");
  }
  return out;
}

/** The codes an entry's hours go to: an admin's resolution if there is one, else the categories'. */
export const effectiveCodes = (e: TimeEntry): string[] => e.resolution?.codes ?? e.codes;

/** Whether an entry still needs an admin: it carries a flag and nobody has resolved it. */
export const needsReview = (e: TimeEntry): boolean => !!e.outlook || (e.flags.length > 0 && !e.resolution);

/** Hours by code for a set of entries. Uncategorized and unresolved unknown codes go under "". */
export function hoursByCode(entries: readonly TimeEntry[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const e of entries) {
    const codes = effectiveCodes(e);
    if (!e.hours || (e.resolution && !codes.length)) continue; // a zero-hour flag, or resolved as not work time
    const keys = codes.length ? codes : [""];
    for (const k of keys) out[k] = round((out[k] ?? 0) + e.hours / keys.length);
  }
  return out;
}

/** Total counted hours: everything but entries resolved as not work time. */
export const countedHours = (entries: readonly TimeEntry[]): number =>
  round(entries.filter((e) => !(e.resolution && !e.resolution.codes.length)).reduce((t, e) => t + e.hours, 0));

// --- Weeks: open, then frozen (AC9) ----------------------------------------------------------------

/** Tuesday 12:00 Central after the week ends (AC9). */
export const DEFAULT_CUTOFF = { daysAfterWeekStart: 8, hour: 12 } as const;

/** The instant a week (named by its Monday) freezes. */
export const weekCutoff = (monday: Iso, cutoff: { daysAfterWeekStart: number; hour: number } = DEFAULT_CUTOFF): number =>
  zonedToUtc(addDays(monday, cutoff.daysAfterWeekStart), cutoff.hour * 3_600_000);

export type WeekState = "open" | "frozen";

/** A week is open (provisional) until its cutoff, then frozen. */
export const weekState = (monday: Iso, nowMs: number, cutoff = DEFAULT_CUTOFF): WeekState => (nowMs < weekCutoff(monday, cutoff) ? "open" : "frozen");

/** The Monday of the week a day falls in. */
export const weekOf = (day: Iso): Iso => mondayOf(day);

/** A short, stable fingerprint (FNV-1a) for imported rows, which have no event id. */
export function fingerprint(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

// --- The Timecard Tool workbook (AC4) --------------------------------------------------------------

/** The columns of a `Data{Name}` table, matched loosely by header text. */
export const TIMECARD_COLUMNS = {
  category: /^categor(y|ies)$/i,
  subject: /^subject$/i,
  start: /^start(\s*time)?$/i,
  end: /^end(\s*time)?$/i,
  allDay: /^all\s*day/i,
  status: /^(status|show\s*as)$/i,
  uploaded: /^upload/i,
  employee: /^employee|^name$/i,
  subCategory: /^sub[-\s]*categor/i,
} as const;
export type TimecardColumn = keyof typeof TIMECARD_COLUMNS;

/** Which column holds what, or null if the start, end or category column is missing. */
export function timecardLayout(headers: readonly string[]): Record<TimecardColumn, number | null> | null {
  const layout = Object.fromEntries(
    (Object.keys(TIMECARD_COLUMNS) as TimecardColumn[]).map((k) => {
      const i = headers.findIndex((h) => TIMECARD_COLUMNS[k].test(h.trim()));
      return [k, i >= 0 ? i : null];
    }),
  ) as Record<TimecardColumn, number | null>;
  return layout.start !== null && layout.end !== null && layout.category !== null ? layout : null;
}

export type Cell = string | number | boolean | null;

export interface TimecardRow {
  /** 1-based row in the table, for pointing back at the workbook. */
  row: number;
  employee: string;
  event: SourceEvent;
  uploaded: number | null;
}

/** Reads a table's rows as events. Rows whose times can't be read are returned as `unreadable`. */
export function readTimecardRows(
  rows: readonly Cell[][],
  layout: Record<TimecardColumn, number | null>,
  sourceZone: string,
  fallbackEmployee: string,
): { rows: TimecardRow[]; unreadable: number[] } {
  const at = (r: readonly Cell[], c: number | null): Cell => (c === null ? null : (r[c] ?? null));
  const text = (v: Cell) => (v === null ? "" : String(v).trim());
  const out: TimecardRow[] = [];
  const unreadable: number[] = [];
  rows.forEach((r, i) => {
    if (r.every((c) => c === null || c === "")) return;
    const startMs = parseSourceTime(at(r, layout.start), sourceZone);
    const endMs = parseSourceTime(at(r, layout.end), sourceZone);
    if (startMs === null || endMs === null) {
      unreadable.push(i + 1);
      return;
    }
    const subject = text(at(r, layout.subject));
    const employee = text(at(r, layout.employee)) || fallbackEmployee;
    const activity = text(at(r, layout.subCategory)) || undefined;
    out.push({
      row: i + 1,
      employee,
      uploaded: layout.uploaded === null ? null : parseSourceTime(at(r, layout.uploaded), sourceZone),
      event: {
        key: `imp:${startMs.toString(36)}:${endMs.toString(36)}:${fingerprint(subject)}`,
        startMs,
        endMs,
        subject,
        categories: parseCategories(at(r, layout.category)),
        showAs: layout.status === null ? "busy" : parseShowAs(at(r, layout.status)),
        isAllDay: parseBoolean(at(r, layout.allDay)),
        ...(activity ? { activity } : {}),
      },
    });
  });
  return { rows: out, unreadable };
}

/**
 * The same event uploaded more than once (same start, end and subject) is kept once: the row with the
 * latest Upload Date, else the later row.
 */
export function dropDuplicateRows(rows: readonly TimecardRow[]): { rows: TimecardRow[]; dropped: number } {
  const byKey = new Map<string, TimecardRow>();
  for (const r of rows) {
    const k = `${r.employee.toLowerCase()}|${r.event.key}`;
    const seen = byKey.get(k);
    if (!seen || (r.uploaded ?? -Infinity) >= (seen.uploaded ?? -Infinity)) byKey.set(k, r);
  }
  const kept = [...byKey.values()].sort((a, b) => a.row - b.row);
  return { rows: kept, dropped: rows.length - kept.length };
}

/**
 * The days an all-day event covers. All-day events start and end at a midnight, but in whichever zone
 * wrote them; noon-to-noon stays inside the intended dates for any US zone or UTC.
 */
export function allDayDays(e: SourceEvent): Iso[] {
  const first = wallClock(e.startMs + 12 * 3_600_000).day;
  const last = wallClock(Math.max(e.startMs, e.endMs - 12 * 3_600_000)).day;
  return [...eachDay(first, last < first ? first : last)];
}

/**
 * An all-day Out of office is ignored for hours, but on a weekday it must come with a timed block
 * saying what it was for: one shown as Out of office, or carrying a time-off code (rev. 1.10). A day
 * without one gets a zero-hour entry flagged `oof-unblocked`, so it reaches the inbox.
 */
function unblockedOutOfOffice(events: readonly SourceEvent[], counted: readonly TimeEntry[], source: TimeEntry["source"]): TimeEntry[] {
  const blocked = new Set(counted.filter((e) => e.showAs === "oof" || e.codes.some((c) => TIME_OFF_CODES.has(c))).map((e) => e.day));
  const out = new Map<Iso, TimeEntry>();
  for (const e of events) {
    if (!e.isAllDay || e.isCancelled || e.showAs !== "oof") continue;
    for (const day of allDayDays(e)) {
      if (!isWeekday(day) || blocked.has(day) || out.has(day)) continue;
      const at = new Date(centralMidnight(day)).toISOString();
      out.set(day, {
        id: `oof:${day}`,
        source,
        start: at,
        end: at,
        day,
        hours: 0,
        codes: [],
        categories: e.categories.filter((c) => !isPersonal(c)),
        showAs: "oof",
        subject: e.subject,
        flags: ["oof-unblocked"],
      });
    }
  }
  return [...out.values()];
}

/** The time-off codes an all-day event can stand for a whole day of (Chris, 2026-10-29). */
const ALL_DAY_TIME_OFF: ReadonlySet<string> = new Set(["3SVAC", "3SSIC"]);

/**
 * An all-day event categorized as vacation or sick: a zero-hour entry on each weekday it covers, marked
 * `allDay`, so time off counts the day as 8 h. Its hours stay out of everything else, like any all-day
 * event, and it says what an all-day Out of office was for.
 */
function allDayTimeOff(events: readonly SourceEvent[], source: TimeEntry["source"], index: CodeIndex): TimeEntry[] {
  const out: TimeEntry[] = [];
  for (const e of events) {
    if (!e.isAllDay || e.isCancelled) continue;
    const categories = e.categories.filter((c) => !isPersonal(c));
    const codes = [...new Set(categories.map((c) => codeForCategory(c, index)).filter((c): c is string => !!c && ALL_DAY_TIME_OFF.has(c)))];
    if (codes.length !== 1) continue;
    for (const day of allDayDays(e)) {
      if (!isWeekday(day)) continue;
      const at = new Date(centralMidnight(day)).toISOString();
      out.push({ id: `allday:${e.key}#${day}`, source, start: at, end: at, day, hours: 0, codes, categories, showAs: e.showAs, subject: e.subject, flags: [], allDay: true });
    }
  }
  return out;
}

/** A person's events to week buckets of flagged entries, with what was left out and why. */
export function entriesByWeek(
  events: readonly SourceEvent[],
  source: TimeEntry["source"],
  index: CodeIndex,
): { weeks: Map<Iso, TimeEntry[]>; excluded: Partial<Record<Exclusion, number>> } {
  const excluded: Partial<Record<Exclusion, number>> = {};
  const all: TimeEntry[] = [];
  for (const e of events) {
    const counted = countEvent(e, source, index);
    if ("excluded" in counted) excluded[counted.excluded] = (excluded[counted.excluded] ?? 0) + 1;
    else all.push(...counted.entries);
  }
  all.push(...allDayTimeOff(events, source, index));
  all.push(...unblockedOutOfOffice(events, all, source));
  const weeks = new Map<Iso, TimeEntry[]>();
  for (const e of flagDays(all)) {
    const w = weekOf(e.day);
    weeks.set(w, [...(weeks.get(w) ?? []), e]);
  }
  for (const list of weeks.values()) list.sort((a, b) => a.start.localeCompare(b.start) || a.id.localeCompare(b.id));
  return { weeks, excluded };
}

/**
 * Splits one person's import into requests of whole, contiguous weeks covering [from, to] (Mondays),
 * each under `maxEvents`. Each request replaces its weeks, so a cut never falls inside an event that
 * runs from one week into the next. Empty weeks are included, so a re-import clears them.
 */
export function importChunks(events: readonly SourceEvent[], from: Iso, to: Iso, maxEvents = 1_500, maxWeeks = 26): { from: Iso; to: Iso; events: SourceEvent[] }[] {
  const byWeek = new Map<Iso, SourceEvent[]>();
  const reach = new Map<Iso, Iso>();
  for (const e of events) {
    const first = weekOf(wallClock(e.startMs).day);
    const last = weekOf(wallClock(Math.max(e.startMs, e.endMs - 1)).day);
    byWeek.set(first, [...(byWeek.get(first) ?? []), e]);
    if (last > (reach.get(first) ?? first)) reach.set(first, last);
  }
  const chunks: { from: Iso; to: Iso; events: SourceEvent[] }[] = [];
  let current: { from: Iso; to: Iso; events: SourceEvent[] } | null = null;
  let spansTo: Iso = from;
  for (let monday = from; monday <= to; monday = addDays(monday, 7)) {
    current ??= { from: monday, to: monday, events: [] };
    current.to = monday;
    current.events.push(...(byWeek.get(monday) ?? []));
    const r = reach.get(monday);
    if (r && r > spansTo) spansTo = r;
    const weeks = Math.round((Date.parse(monday) - Date.parse(current.from)) / 604_800_000) + 1;
    const next = addDays(monday, 7);
    const full = current.events.length + (byWeek.get(next)?.length ?? 0) > maxEvents || weeks >= maxWeeks;
    if (full && spansTo <= monday) {
      chunks.push(current);
      current = null;
    }
  }
  if (current) chunks.push(current);
  return chunks;
}
